import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, Check, Loader2 } from 'lucide-react';
import axios from 'axios';
import LeadMagnetModal from '../modals/LeadMagnetModal';
import { useLanguage } from '../../context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    weddingDate: '',
    preferredTime: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showLeadMagnet, setShowLeadMagnet] = useState(false);
  const [error, setError] = useState('');
  const { t } = useLanguage();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      // Include preferred time in the message for backend
      const dataToSend = {
        ...formData,
        message: formData.preferredTime 
          ? `${formData.message}\n\nHorário preferido para contacto: ${formData.preferredTime}`
          : formData.message
      };
      await axios.post(`${API}/contacts`, dataToSend);
      setIsSubmitted(true);
      
      // Show lead magnet modal after successful submission
      setTimeout(() => {
        setShowLeadMagnet(true);
      }, 1500);
      
      setFormData({
        name: '',
        email: '',
        phone: '',
        weddingDate: '',
        preferredTime: '',
        message: '',
      });
    } catch (err) {
      console.error('Error submitting contact:', err);
      setError(t('contact.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = {
    email: "info@almaleveweddings.com",
    phone: "+351 936 447 633",
    location: "Lisboa, Portugal"
  };

  return (
    <section id="contact" className="section-padding bg-transparent">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Info Column */}
          <div>
            {/* Section Header */}
            <p className="text-caption text-[#C4B5A0] mb-4">
              {t('contact.label')}
            </p>
            <h2 className="heading-secondary text-4xl md:text-5xl text-[#2C2C2C] mb-6">
              {t('contact.title')}
            </h2>
            <div className="divider !mx-0 !ml-0" />
            <p className="text-body text-[#6B4E37] mb-10 max-w-lg">
              {t('contact.description')}
            </p>

            {/* Contact Info */}
            <div className="space-y-6 mb-10">
              <a
                href={`mailto:${contactInfo.email}`}
                className="flex items-center gap-4 text-[#6B4E37] hover:text-[#2C2C2C] transition-colors group"
              >
                <div className="w-12 h-12 rounded-full bg-[#D4C4B0]/30 flex items-center justify-center group-hover:bg-[#C4B5A0] transition-colors">
                  <Mail size={20} className="group-hover:text-white transition-colors" />
                </div>
                <span className="text-body">{contactInfo.email}</span>
              </a>

              <a
                href={`tel:${contactInfo.phone}`}
                className="flex items-center gap-4 text-[#6B4E37] hover:text-[#2C2C2C] transition-colors group"
              >
                <div className="w-12 h-12 rounded-full bg-[#D4C4B0]/30 flex items-center justify-center group-hover:bg-[#C4B5A0] transition-colors">
                  <Phone size={20} className="group-hover:text-white transition-colors" />
                </div>
                <span className="text-body">{contactInfo.phone}</span>
              </a>

              <div className="flex items-center gap-4 text-[#6B4E37]">
                <div className="w-12 h-12 rounded-full bg-[#D4C4B0]/30 flex items-center justify-center">
                  <MapPin size={20} />
                </div>
                <div>
                  <span className="text-body block">{contactInfo.location}</span>
                  <span className="text-sm text-[#8B7D6B]">{t('contact.availability')}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Form Column */}
          <div>
            <div className="bg-[#FAF9F7] rounded-2xl shadow-xl p-8 md:p-10">
              {isSubmitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-full bg-[#B5C4B1]/30 flex items-center justify-center mx-auto mb-6">
                    <Check size={32} className="text-[#6B4E37]" />
                  </div>
                  <h3 className="font-['Cormorant_Garamond'] text-2xl text-[#2C2C2C] mb-3">
                    {t('contact.success')}
                  </h3>
                  <p className="text-body text-[#8B7D6B] mb-6">
                    {t('contact.successMessage')}
                  </p>
                  <button
                    onClick={() => setIsSubmitted(false)}
                    className="btn-secondary"
                    data-testid="new-message-btn"
                  >
                    {t('contact.newMessage')}
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm text-[#8B7D6B] mb-2">
                        {t('contact.name')} *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="form-input"
                        placeholder={t('contact.namePlaceholder')}
                        data-testid="contact-name"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm text-[#8B7D6B] mb-2">
                        {t('contact.email')} *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="form-input"
                        placeholder={t('contact.emailPlaceholder')}
                        data-testid="contact-email"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="phone" className="block text-sm text-[#8B7D6B] mb-2">
                        {t('contact.phone')} *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="form-input"
                        placeholder={t('contact.phonePlaceholder')}
                        data-testid="contact-phone"
                      />
                    </div>
                    <div>
                      <label htmlFor="weddingDate" className="block text-sm text-[#8B7D6B] mb-2">
                        {t('contact.weddingDate')} *
                      </label>
                      <input
                        type="text"
                        id="weddingDate"
                        name="weddingDate"
                        value={formData.weddingDate}
                        onChange={handleChange}
                        required
                        className="form-input"
                        placeholder={t('contact.weddingDatePlaceholder')}
                        data-testid="contact-wedding-date"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="preferredTime" className="block text-sm text-[#8B7D6B] mb-2">
                      {t('contact.preferredTime')} *
                    </label>
                    <select
                      id="preferredTime"
                      name="preferredTime"
                      value={formData.preferredTime}
                      onChange={handleChange}
                      required
                      className="form-input cursor-pointer"
                      data-testid="contact-preferred-time"
                    >
                      <option value="">{t('contact.selectOption')}</option>
                      <option value={t('contact.morning')}>{t('contact.morning')}</option>
                      <option value={t('contact.lunch')}>{t('contact.lunch')}</option>
                      <option value={t('contact.afternoon')}>{t('contact.afternoon')}</option>
                      <option value={t('contact.evening')}>{t('contact.evening')}</option>
                      <option value={t('contact.noPreference')}>{t('contact.noPreference')}</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm text-[#8B7D6B] mb-2">
                      {t('contact.message')}
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={5}
                      className="form-input resize-none"
                      placeholder={t('contact.messagePlaceholder')}
                      data-testid="contact-message"
                    />
                  </div>

                  {error && (
                    <p className="text-red-500 text-sm" data-testid="contact-error">{error}</p>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                    data-testid="contact-submit"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        {t('contact.sending')}
                      </>
                    ) : (
                      <>
                        <Send size={18} />
                        {t('contact.send')}
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Lead Magnet Modal */}
      <LeadMagnetModal 
        isOpen={showLeadMagnet} 
        onClose={() => setShowLeadMagnet(false)} 
      />
    </section>
  );
};

export default ContactSection;
